import { channelsInstance } from '@/api/instances'
import { CHANNEL_CATEGORIES } from '@/models/channels'
import DatabaseFactory from '@/wrappers/database'
import { addErrorLog } from '@/wrappers/database/utils/addLog'
import Logger, {
  EventModel,
  EventName,
  LogLevel,
  ProviderType
} from '@/wrappers/logger'

const setRawCurrentChannel = async (store, currentChannel) => {
  store.commit('setRawCurrentChannel', currentChannel)
}

const fetchChannel = async (store, channelId) => {
  store.commit('clearChannelListener')

  let fistIteration = true

  const database = new DatabaseFactory(
    `channels/${channelId}`
  ).getReadDatabase()

  const listener = await database.getOneStream(
    doc => {
      if (!doc.exists()) return

      if (doc.data().personId !== store.rootState.pillars.personId) {
        store.commit('ui/channel/setMixedDataError', true, {
          root: true
        })

        Logger.addLog(
          EventName.INTERACTION_ERROR,
          {
            model: EventModel.CHANNELS,
            modelId: channelId,
            data: {
              context: 'mexed_data_channel',
              channelPersonId: doc.data().personId,
              pillarsPersonId: store.rootState.pillars.personId,
              snapshotChannelId: doc.id,
              meta: {
                fistIteration,
                url: window.location.href,
                pillars: store.rootGetters['pillars/pillars']
              }
            }
          },
          {
            isAction: true,
            logLevel: LogLevel.ERROR
          },
          [ProviderType.DATADOG]
        )
        return
      }

      store.commit('setCurrentChannel', doc)

      const { controlChannel, currentChannel } = store.state
      if (controlChannel?.id === currentChannel.id) {
        store.commit('setControlChannel', {
          ...currentChannel,
          name: currentChannel.name || controlChannel.name
        })
      }

      store.dispatch(
        'member/loadByChannelPersonIds',
        {
          channelPersonIds: [currentChannel.channelPersonId],
          notifications: true,
          leanResponse: true
        },
        { root: true }
      )
      store.dispatch('member/loadMembersPhotos', true, {
        root: true
      })

      if (fistIteration) {
        fistIteration = false
      }
    },
    error =>
      addErrorLog({
        context: 'channel/actions - fetchChannel',
        channelId,
        error
      })
  )
  store.commit('setChannelListener', listener)
}

const fetchFileUrl = async (store, url) => {
  try {
    const file = await store.dispatch('file/getFile', url, { root: true })
    return file.url
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'channel/fetchFileUrl' },
      { root: true }
    )
  }
}

const fetchHealthTags = async (store, personId) => {
  store.commit('setHealthTags', [])

  try {
    const tags = await channelsInstance.get(`persons/${personId}/health_tags`)

    store.commit('setHealthTags', tags)
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'channel/fetchHealthTags' },
      { root: true }
    )
  }
}

const renameChannel = async (store, { channelId, name, personId }) => {
  if (channelId) {
    const isValid = validateChannel(store, store.state.controlChannel)
    if (!isValid) {
      return false
    }

    try {
      await channelsInstance.put(`channels/${channelId}/rename`, {
        json: { name }
      })

      fetchMemberChannels(store, personId)
    } catch (error) {
      store.dispatch(
        'errors/setError',
        { error, request: 'channel/renameChannel' },
        { root: true }
      )
      return false
    }
    return true
  }
  store.commit('updateControlChannel', { name })
  return true
}

const toggleChannelHighlight = async (store, channelId) => {
  try {
    const user = store.rootGetters['user/user']
    await channelsInstance.put(`channels/${channelId}/highlight`, {
      json: { staffId: user.id }
    })
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'channel/toggleChannelHighlight' },
      { root: true }
    )
  }
}

const archiveChannel = async (store, channelId) => {
  try {
    await channelsInstance.put(`channels/${channelId}/archive`)
    return true
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'channel/archiveChannel' },
      { root: true }
    )
    return false
  }
}

const classifyAndArchiveChannel = async (
  store,
  { channelId, akinatorSetupId, suggestedTheme, theme, reason, screenOpenedAt }
) => {
  try {
    await channelsInstance.put(`channels/${channelId}/archive_administrative`, {
      json: {
        akinatorSetupId,
        suggestedTheme,
        theme,
        reason,
        screenOpenedAt
      }
    })
    return true
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'channel/classifyAndArchiveChannel' },
      { root: true }
    )
    return false
  }
}

const updateChannelAssessment = async (store, { channelId, assessment }) => {
  try {
    await channelsInstance.post(`channels/screening/assessment`, {
      json: { channelId, assessment }
    })
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'channel/updateChannelAssessment' },
      { root: true }
    )
  }
}

const setMember = (store, member) => {
  store.commit('updateControlChannel', member)
}

const updateChannelTags = async (store, { channelId, tags }) => {
  if (channelId) {
    try {
      await channelsInstance.put(`channels/${channelId}/tags`, {
        json: { tags }
      })
    } catch (error) {
      store.dispatch(
        'errors/setError',
        { error, request: 'channel/updateChannelTags' },
        { root: true }
      )
    }
    return
  }
  store.commit('updateControlChannel', { tags })
}

const updateStaffs = (store, { channelId, staffId, staff, add }) => {
  if (channelId) {
    if (add) {
      store.dispatch('addStaffToChannel', { channelId, staffId })
      return
    }
    store.dispatch('removeStaffFromChannel', { channelId, staffId })
    return
  }
  store.commit('updateControlChannel', { staff })
}

const addStaffToChannel = async (store, { channelId, staffId }) => {
  try {
    await channelsInstance.put(`channels/${channelId}/add/${staffId}`)
    return true
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'channel/addStaffToChannel' },
      { root: true }
    )
    return false
  }
}

const removeStaffFromChannel = async (store, { channelId, staffId }) => {
  try {
    if (!validateStaff(store.state.controlChannel)) {
      setErrorMessage(
        store,
        'Não é possível remover profissional de saúde do canal, o canal não pode ficar vazio'
      )

      return false
    }
    await channelsInstance.put(`channels/${channelId}/remove/${staffId}`)
    return true
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'channel/removeStaffFromChannel' },
      { root: true }
    )
  }
}

const createChannel = async (store, data) => {
  try {
    const channelResponse = await channelsInstance.post('channels', {
      json: data
    })

    fetchMemberChannels(store, data.personId)
    return channelResponse
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'channel/createChannel' },
      { root: true }
    )
  }
}

const mergeChannels = async (store, { currentChannelId, mergeChannelId }) => {
  try {
    await channelsInstance.post(
      `channels/${currentChannelId}/merge/${mergeChannelId}`
    )
    return true
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'channel/mergeChannels' },
      { root: true }
    )
  }
}

const updateMergedChannel = (store, channelToMerge) => {
  store.commit('setMergeChannel')
  store.commit('updateControlChannel', { name: '' })

  if (!channelToMerge.id) {
    store.commit('updateControlChannel', { name: channelToMerge.name })
    return
  }

  store.commit('setMergeChannel', channelToMerge)
}

const giveOwnership = async (store, { channelId, staffId }) => {
  try {
    await channelsInstance.put(
      `channels/${channelId}/give_ownership/${staffId}`
    )
    return true
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'channel/giveOwnership' },
      { root: true }
    )
  }
}

const fetchMemberChannels = (store, personId) => {
  if (personId) {
    store.commit('pillars/channel/member/setMemberChannels', [], {
      root: true
    })
    store.dispatch(
      'pillars/channel/member/fetchMemberChannels',
      { personId },
      {
        root: true
      }
    )
  }
}

const validateChannel = (store, channel) => {
  if (!validateDemands(channel)) {
    setErrorMessage(
      store,
      'Antes de nomear o chat você precisa criar uma consulta'
    )
    return false
  }
  return true
}

const validateStaff = channel => {
  return channel && Object.keys(channel.staff).length > 1
}

const validateDemands = channel => {
  return (
    channel.category === CHANNEL_CATEGORIES.ADMINISTRATIVE ||
    (channel.demands && channel.demands.length)
  )
}

const setErrorMessage = (store, message) => {
  store.dispatch('errors/setError', { message }, { root: true })
}

const reroutingChat = async (
  store,
  { channelId, category, subCategory, reason }
) => {
  try {
    await channelsInstance.post(`channels/${channelId}/re_routing/v2`, {
      json: {
        category,
        subCategory,
        reason
      }
    })
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        request: 'pillars/channel/reroutingChat',
        message: 'Ocorreu um erro ao redirecionar o chat'
      },
      { root: true }
    )
  }
}

const dischargeChat = async (store, channel) => {
  const isValid = validateChannel(store, channel)
  if (!isValid) {
    return false
  }

  if (!channel.canBeArchived) {
    store.dispatch(
      'errors/setError',
      {
        message:
          'Você precisa publicar o registro clínico antes de encerrar o atendimento'
      },
      { root: true }
    )
    return false
  }
  try {
    await channelsInstance.put(`channels/${channel.id}/discharge`, {
      json: {
        name: channel.name,
        kind: channel.kind,
        category: channel.category,
        subCategory: channel.subCategory
      }
    })
    return true
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        request: 'pillars/channel/dischargeChat',
        message: 'Ocorreu um erro ao encerrar o chat'
      },
      { root: true }
    )
  }
}

const fetchZendeskAssignmentTags = async (store, { channelId }) => {
  Logger.addLog(EventName.USER_INTERACTION_FORM, {
    model: EventModel.CHANNEL,
    modelId: channelId,
    data: {
      context: 'redirect-to-n2'
    }
  })

  try {
    const tags = await channelsInstance.get(
      `channels/${channelId}/zendesk_assignment_tags`
    )

    return tags
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        request: 'channel/zendesk_assignment_tags',
        message:
          'Ocorreu um erro ao carregar as informações de atendimento do Zendesk'
      },
      { root: true }
    )
  }
}

const assignTicketInZendesk = async (store, { channelId, ticket }) => {
  try {
    await channelsInstance.post(
      `channels/${channelId}/assign_ticket_in_zendesk`,
      {
        json: ticket
      }
    )

    return true
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        request: 'channel/assign_ticket_in_zendesk',
        message: 'Ocorreu um erro ao criar o ticket no Zendesk'
      },
      { root: true }
    )
  }
}

const getAkinatorSuggestedTheme = async (store, channelId) => {
  const abortController = new AbortController()
  const timeout = setTimeout(() => {
    abortController.abort()
  }, 7000)

  try {
    const suggetion = await channelsInstance.get(
      `channels/${channelId}/administrative_classify`,
      { signal: abortController.signal }
    )
    clearTimeout(timeout)
    return suggetion
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        request: 'channel/getAkinatorSuggestedTheme',
        message:
          'Desculpe. Não foi possível sugerir o tema. Preencha os campos manualmente.'
      },
      { root: true }
    )
  }
}

const getAllContactThemes = async store => {
  try {
    const contactThemes = await channelsInstance.get(`channel_contact_themes/`)

    return contactThemes
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        request: 'channel_contact_themes/',
        message: 'Ocorreu um erro ao pegar os temas de contato'
      },
      { root: true }
    )
  }
}

const clearState = store => {
  store.commit('clearChannelListener')
  store.commit('pillars/channel/message/clearMessagesListener', null, {
    root: true
  })
  store.dispatch('message/clearMessages')
  store.commit('resetCurrentChannel')
  store.commit('setHealthTags', [])
  store.dispatch('member/clearState')
  store.commit('setMergeChannel', {})
  store.commit('setControlChannel', {})
}

export default {
  fetchChannel,
  renameChannel,
  toggleChannelHighlight,
  archiveChannel,
  classifyAndArchiveChannel,
  updateChannelAssessment,
  fetchFileUrl,
  updateChannelTags,
  addStaffToChannel,
  removeStaffFromChannel,
  createChannel,
  mergeChannels,
  giveOwnership,
  fetchHealthTags,
  setMember,
  updateStaffs,
  updateMergedChannel,
  clearState,
  reroutingChat,
  dischargeChat,
  fetchZendeskAssignmentTags,
  assignTicketInZendesk,
  getAkinatorSuggestedTheme,
  getAllContactThemes,
  setRawCurrentChannel
}
